<template>
  <div class="icon-picker-wrapper">
    <!-- Campo de Texto com Preview -->
    <v-text-field
        :value="value"
        :label="label"
        :required="required"
        :disabled="disabled"
        readonly
        outlined
        dense
        @click="openDialog"
        :error-messages="errorMessages"
    >
      <template v-slot:prepend-inner>
        <v-icon v-if="value" :color="previewColor">
          {{ value }}
        </v-icon>
        <v-icon v-else color="grey">
          mdi-emoticon-outline
        </v-icon>
      </template>
      <template v-slot:append>
        <v-btn
            icon
            small
            @click.stop="openDialog"
            :disabled="disabled"
        >
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-text-field>

    <!-- Dialog de Seleção -->
    <v-dialog
        v-model="dialog"
        max-width="800px"
        scrollable
    >
      <v-card>
        <v-card-title class="text-h6 primary white--text">
          <v-icon left color="white">mdi-shape</v-icon>
          Selecionar Ícone
          <v-spacer></v-spacer>
          <v-btn
              icon
              dark
              @click="dialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Campo de Busca -->
          <v-text-field
              v-model="busca"
              label="Buscar ícone"
              placeholder="Ex: home, user, file..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              autofocus
              class="mb-2"
          ></v-text-field>

          <!-- Categorias -->
          <v-chip-group
              v-model="categoriaAtiva"
              active-class="primary--text"
              class="mb-4"
          >
            <v-chip
                v-for="cat in categorias"
                :key="cat.value"
                small
                outlined
            >
              <v-icon left small>{{ cat.icon }}</v-icon>
              {{ cat.label }}
            </v-chip>
          </v-chip-group>

          <!-- Preview do Ícone Selecionado -->
          <v-card
              v-if="iconeTemporario"
              outlined
              class="mb-4 pa-4 text-center"
          >
            <v-icon size="64" :color="previewColor">
              {{ iconeTemporario }}
            </v-icon>
            <p class="text-caption mt-2 mb-0">{{ iconeTemporario }}</p>
          </v-card>

          <!-- Grid de Ícones -->
          <div class="icons-grid-wrapper">
            <v-alert
                v-if="iconesFiltrados.length === 0"
                type="info"
                text
                dense
            >
              Nenhum ícone encontrado para "{{ busca }}"
            </v-alert>

            <div v-else class="icons-grid">
              <v-tooltip
                  v-for="icone in iconesFiltrados"
                  :key="icone.nome"
                  bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                      v-bind="attrs"
                      v-on="on"
                      :class="['icon-item', { 'selected': iconeTemporario === icone.nome }]"
                      @click="selecionarIcone(icone.nome)"
                  >
                    <v-icon :color="iconeTemporario === icone.nome ? 'primary' : 'grey darken-1'">
                      {{ icone.nome }}
                    </v-icon>
                  </div>
                </template>
                <span>{{ icone.nome }}</span>
              </v-tooltip>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn
              text
              @click="limparSelecao"
          >
            Limpar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
              text
              @click="dialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
              color="primary"
              @click="confirmarSelecao"
              :disabled="!iconeTemporario"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'IconPicker',

  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Ícone'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    previewColor: {
      type: String,
      default: 'primary'
    },
    errorMessages: {
      type: [String, Array],
      default: ''
    }
  },

  data() {
    return {
      dialog: false,
      busca: '',
      iconeTemporario: '',
      categoriaAtiva: 0,

      categorias: [
        { label: 'Todos', value: 'all', icon: 'mdi-apps' },
        { label: 'Negócios', value: 'business', icon: 'mdi-briefcase' },
        { label: 'Arquivos', value: 'files', icon: 'mdi-file' },
        { label: 'Usuário', value: 'user', icon: 'mdi-account' },
        { label: 'Interface', value: 'ui', icon: 'mdi-monitor' },
        { label: 'Segurança', value: 'security', icon: 'mdi-shield' }
      ],

      // Lista de ícones populares do Material Design Icons
      icones: [
        // Negócios
        { nome: 'mdi-briefcase', categoria: 'business', tags: ['maleta', 'trabalho', 'negócio'] },
        { nome: 'mdi-cash-multiple', categoria: 'business', tags: ['dinheiro', 'financeiro'] },
        { nome: 'mdi-chart-line', categoria: 'business', tags: ['gráfico', 'relatório'] },
        { nome: 'mdi-bank', categoria: 'business', tags: ['banco', 'financeiro'] },
        { nome: 'mdi-calculator', categoria: 'business', tags: ['calculadora', 'contabilidade'] },
        { nome: 'mdi-cash', categoria: 'business', tags: ['dinheiro', 'pagamento'] },
        { nome: 'mdi-currency-usd', categoria: 'business', tags: ['dólar', 'moeda'] },
        { nome: 'mdi-store', categoria: 'business', tags: ['loja', 'comércio'] },
        { nome: 'mdi-cart', categoria: 'business', tags: ['loja', 'comércio'] },

        // Arquivos
        { nome: 'mdi-file-document', categoria: 'files', tags: ['arquivo', 'documento'] },
        { nome: 'mdi-file-pdf-box', categoria: 'files', tags: ['pdf', 'documento'] },
        { nome: 'mdi-file-word-box', categoria: 'files', tags: ['word', 'doc'] },
        { nome: 'mdi-file-excel-box', categoria: 'files', tags: ['excel', 'planilha'] },
        { nome: 'mdi-folder', categoria: 'files', tags: ['pasta', 'diretório'] },
        { nome: 'mdi-file-multiple', categoria: 'files', tags: ['múltiplos', 'arquivos'] },
        { nome: 'mdi-cloud-upload', categoria: 'files', tags: ['upload', 'enviar'] },
        { nome: 'mdi-download', categoria: 'files', tags: ['baixar', 'download'] },

        // Usuário
        { nome: 'mdi-account', categoria: 'user', tags: ['usuário', 'pessoa'] },
        { nome: 'mdi-account-group', categoria: 'user', tags: ['grupo', 'equipe'] },
        { nome: 'mdi-account-circle', categoria: 'user', tags: ['perfil', 'avatar'] },
        { nome: 'mdi-account-tie', categoria: 'user', tags: ['profissional', 'executivo'] },
        { nome: 'mdi-badge-account', categoria: 'user', tags: ['crachá', 'identificação'] },

        // Interface
        { nome: 'mdi-home', categoria: 'ui', tags: ['início', 'casa'] },
        { nome: 'mdi-cog', categoria: 'ui', tags: ['configuração', 'engrenagem'] },
        { nome: 'mdi-bell', categoria: 'ui', tags: ['notificação', 'sino'] },
        { nome: 'mdi-menu', categoria: 'ui', tags: ['menu', 'hamburguer'] },
        { nome: 'mdi-magnify', categoria: 'ui', tags: ['buscar', 'pesquisar'] },
        { nome: 'mdi-plus', categoria: 'ui', tags: ['adicionar', 'novo'] },
        { nome: 'mdi-pencil', categoria: 'ui', tags: ['editar', 'modificar'] },
        { nome: 'mdi-delete', categoria: 'ui', tags: ['excluir', 'remover'] },
        { nome: 'mdi-check', categoria: 'ui', tags: ['confirmar', 'ok'] },
        { nome: 'mdi-close', categoria: 'ui', tags: ['fechar', 'cancelar'] },

        // Segurança
        { nome: 'mdi-shield-check', categoria: 'security', tags: ['proteção', 'segurança'] },
        { nome: 'mdi-shield-account', categoria: 'security', tags: ['proteção', 'usuário'] },
        { nome: 'mdi-lock', categoria: 'security', tags: ['travar', 'cadeado'] },
        { nome: 'mdi-key', categoria: 'security', tags: ['chave', 'senha'] },
        { nome: 'mdi-security', categoria: 'security', tags: ['segurança', 'proteção'] },
        { nome: 'mdi-alert', categoria: 'security', tags: ['alerta', 'aviso'] },

        // Qualidade
        { nome: 'mdi-check-circle', categoria: 'business', tags: ['qualidade', 'aprovado'] },
        { nome: 'mdi-star', categoria: 'business', tags: ['favorito', 'destaque'] },
        { nome: 'mdi-medal', categoria: 'business', tags: ['medalha', 'prêmio'] },
        { nome: 'mdi-clipboard-check', categoria: 'business', tags: ['checklist', 'verificação'] },

        // Tecnologia
        { nome: 'mdi-laptop', categoria: 'ui', tags: ['computador', 'notebook'] },
        { nome: 'mdi-monitor', categoria: 'ui', tags: ['monitor', 'tela'] },
        { nome: 'mdi-cellphone', categoria: 'ui', tags: ['celular', 'telefone'] },
        { nome: 'mdi-printer', categoria: 'ui', tags: ['impressora', 'imprimir'] },
        { nome: 'mdi-server', categoria: 'ui', tags: ['servidor', 'database'] },

        // Comunicação
        { nome: 'mdi-email', categoria: 'ui', tags: ['email', 'mensagem'] },
        { nome: 'mdi-phone', categoria: 'ui', tags: ['telefone', 'ligar'] },
        { nome: 'mdi-message', categoria: 'ui', tags: ['mensagem', 'chat'] },
        { nome: 'mdi-comment', categoria: 'ui', tags: ['comentário', 'feedback'] },

        // Diversos
        { nome: 'mdi-calendar', categoria: 'ui', tags: ['calendário', 'data'] },
        { nome: 'mdi-clock', categoria: 'ui', tags: ['relógio', 'hora'] },
        { nome: 'mdi-map-marker', categoria: 'ui', tags: ['localização', 'mapa'] },
        { nome: 'mdi-bookmark', categoria: 'ui', tags: ['favorito', 'marcador'] },
        { nome: 'mdi-flag', categoria: 'ui', tags: ['bandeira', 'marcação'] },
        { nome: 'mdi-tag', categoria: 'ui', tags: ['etiqueta', 'tag'] },
        { nome: 'mdi-heart', categoria: 'ui', tags: ['coração', 'favorito'] },
        { nome: 'mdi-information', categoria: 'ui', tags: ['informação', 'ajuda'] }
      ]
    }
  },

  computed: {
    iconesFiltrados() {
      let resultado = this.icones

      // Filtrar por categoria
      const categoriaFiltro = this.categorias[this.categoriaAtiva].value
      if (categoriaFiltro !== 'all') {
        resultado = resultado.filter(icone => icone.categoria === categoriaFiltro)
      }

      // Filtrar por busca
      if (this.busca && this.busca.trim()) {
        const termo = this.busca.toLowerCase().trim()
        resultado = resultado.filter(icone => {
          const nomeCombina = icone.nome.toLowerCase().includes(termo)
          const tagsCombina = icone.tags.some(tag => tag.includes(termo))
          return nomeCombina || tagsCombina
        })
      }

      return resultado
    }
  },

  watch: {
    dialog(val) {
      if (val) {
        this.iconeTemporario = this.value
        this.busca = ''
        this.categoriaAtiva = 0
      }
    }
  },

  methods: {
    openDialog() {
      if (!this.disabled) {
        this.dialog = true
      }
    },

    selecionarIcone(nomeIcone) {
      this.iconeTemporario = nomeIcone
    },

    confirmarSelecao() {
      this.$emit('input', this.iconeTemporario)
      this.$emit('change', this.iconeTemporario)
      this.dialog = false
    },

    limparSelecao() {
      this.iconeTemporario = ''
    }
  }
}
</script>

<style scoped>
.icon-picker-wrapper {
  width: 100%;
}

.icons-grid-wrapper {
  max-height: 400px;
  overflow-y: auto;
}

.icons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 8px;
  padding: 8px 0;
}

.icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fafafa;
}

.icon-item:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.05);
  transform: scale(1.05);
}

.icon-item.selected {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.1);
  border-width: 3px;
}

/* Scrollbar customizada */
.icons-grid-wrapper::-webkit-scrollbar {
  width: 8px;
}

.icons-grid-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.icons-grid-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.icons-grid-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>